import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import keyMirror from 'keymirror';
import bindAll from 'lodash.bindall';

import DeviceSelectStep from './device-select-step.jsx';
import SerialStep from './serial-step.jsx';
import ScanningStep from './scanning-step.jsx';
import ConnectingStep from './connecting-step.jsx';
import ConnectedStep from './connected-step.jsx';
import ErrorStep from './error-step.jsx';
import UnavailableStep from './unavailable-step.jsx';
import UpdatePeripheralStep from './update-peripheral-step.jsx';
import DeviceSelectionStep from './device-selection-step.jsx';

import styles from './connection-modal.css';

import SerialHelper from '../../lib/serial-helper';
import VMExtensionBridge from '../../lib/vm-extension-bridge';

const PHASES = keyMirror({
    deviceSelection: null,
    scanning: null,
    connecting: null,
    connected: null,
    error: null,
    unavailable: null,
    updatePeripheral: null,
    serial: null
});

class ConnectionModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleScanning',
            'handleConnecting',
            'handleConnected',
            'handleError',
            'handleHelp',
            'handleSelectDevice',
            'handleDeviceSelect',
            'handleSerialSelect',
            'handleConnectSerial',
            'handleCancel',
            'handleBack'
        ]);
        this.state = {
            phase: props.connectionModalPeripheralName ? PHASES.scanning : PHASES.deviceSelection,
            peripheralName: props.connectionModalPeripheralName,
            peripheralId: props.connectionModalPeripheralId,
            extensionId: props.extensionId,
            error: null
        };

        // Serial bağlantısı için gerekli değişkenler
        this.serialHelper = new SerialHelper();
        this.vmExtensionBridge = null;
        
        if (this.props.vm) {
            this.vmExtensionBridge = new VMExtensionBridge(this.props.vm);
        }
    }
    
    componentWillUnmount () {
        // SerialHelper bağlantısını güvenli şekilde kapat
        if (this.serialHelper && typeof this.serialHelper.isConnected === 'function') {
            if (this.serialHelper.isConnected()) {
                this.serialHelper.disconnect().catch(error => {
                    console.error('SerialHelper disconnect hatası:', error);
                });
            }
        }
    }
    
    handleScanning () {
        this.setState({
            phase: PHASES.scanning
        });
    }
    handleConnecting (peripheralId, peripheralName = null) {
        this.setState({
            phase: PHASES.connecting,
            peripheralId: peripheralId,
            peripheralName: peripheralName
        });
    }
    handleConnected () {
        this.setState({
            phase: PHASES.connected
        });
    }
    handleError (error) {
        this.setState({
            phase: PHASES.error,
            error: error
        });
    }
    handleHelp () {
        // @todo: implement this function
    }
    handleCancel () {
        try {
            // Güvenli şekilde modal'ı kapat
            if (this.props.onCancel && typeof this.props.onCancel === 'function') {
                this.props.onCancel();
            }
        } catch (error) {
            console.error('Modal kapatma hatası:', error);
            // Hata durumunda da modal'ı kapatmaya çalış
            window.location.reload();
        }
    }
    handleBack () {
        try {
            if (this.state.phase === PHASES.serial || this.state.phase === PHASES.scanning) {
                // Serial veya scanning'den device selection'a dön
                this.setState({
                    phase: PHASES.deviceSelection,
                    error: null
                });
            } else {
                // Diğer durumlar için modal'ı kapat
                this.handleCancel();
            }
        } catch (error) {
            console.error('Geri dönüş hatası:', error);
            this.handleCancel();
        }
    }
    handleSelectDevice (deviceType) {
        if (deviceType === 'serial') {
            this.setState({
                phase: PHASES.serial
            });
        } else {
            this.handleScanning();
        }
    }
    handleDeviceSelect () {
        this.setState({
            phase: PHASES.deviceSelection
        });
    }
    handleSerialSelect () {
        this.setState({
            phase: PHASES.serial
        });
    }
    
    async handleConnectSerial (baudRate) {
        try {
            console.log('Serial bağlantı başlatılıyor, baudRate:', baudRate);
            
            // Doğrudan SerialHelper kullan
            if (this.serialHelper) {
                // Önce port seçimi yap
                await this.serialHelper.requestPort();
                
                // Sonra bağlantı kur
                const success = await this.serialHelper.connect(baudRate);
                
                if (success) {
                    console.log('Serial bağlantı başarılı');
                    this.setState({
                        phase: PHASES.connected,
                        peripheralName: 'Seri Port Cihazı'
                    });
                    return true;
                } else {
                    this.handleError('Seri port bağlantı hatası');
                    return false;
                }
            } else {
                this.handleError('SerialHelper oluşturulamadı');
                return false;
            }
        } catch (error) {
            console.error('Serial bağlantı hatası:', error);
            this.handleError(`Bağlantı hatası: ${error.message}`);
            return false;
        }
    }
    
    render () {
        return (
            <div 
                className={styles.modalOverlay} 
                onClick={(e) => {
                    // Sadece overlay'e tıklandığında kapat
                    if (e.target === e.currentTarget) {
                        this.handleCancel();
                    }
                }}
            >
                <div 
        className={styles.modalContent}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button 
                        className={styles.closeButton}
                        onClick={this.handleCancel}
                        title="Kapat"
                    >
                        ✕
                    </button>
                    
                    {this.state.phase === PHASES.deviceSelection && (
                        <DeviceSelectStep
                            onSelectDevice={this.handleSelectDevice}
                            onCancel={this.handleCancel}
                        />
                    )}
                    {this.state.phase === PHASES.serial && (
                        <SerialStep
                            isSerialSupported={this.serialHelper.isSerialSupported()}
                            extensionId={this.props.extensionId}
                            onConnectSerial={this.handleConnectSerial}
                            onConnected={this.handleConnected}
                            onCancel={this.handleBack}
                        />
                    )}
                    {this.state.phase === PHASES.scanning && (
                        <ScanningStep
                            connectionSmallIconURL={this.props.connectionSmallIconURL}
                            connectionTipIconURL={this.props.connectionTipIconURL}
                            extensionId={this.state.extensionId}
                            vm={this.props.vm}
                            onConnecting={this.handleConnecting}
                            onRefresh={() => this.handleScanning()}
                            onCancel={this.handleBack}
                            scanning={true}
                            onConnected={this.handleConnected}
                            onUpdatePeripheral={() => {}}
                            peripheralList={[]}
                            phase="scan"
                            showUpdate={false}
                            isRefreshing={false}
                        />
                    )}
                    {this.state.phase === PHASES.connecting && (
                        <ConnectingStep
                            connectionTipIconURL={this.props.connectionTipIconURL}
                            peripheralName={this.state.peripheralName}
                            onCancel={this.handleCancel}
                        />
                    )}
                    {this.state.phase === PHASES.connected && (
                        <ConnectedStep
                            connectionIconURL={this.props.connectionIconURL}
                            onCancel={this.handleCancel}
                            peripheralName={this.state.peripheralName}
                        />
                    )}
                    {this.state.phase === PHASES.error && (
                        <ErrorStep
                            error={this.state.error}
                            onCancel={this.handleCancel}
                            onHelp={this.handleHelp}
                            onScanning={this.handleScanning}
                        />
                    )}
                    {this.state.phase === PHASES.unavailable && (
                        <UnavailableStep
                            connectionTipIconURL={this.props.connectionTipIconURL}
                            onBack={this.handleBack}
                            onHelp={this.handleHelp}
                        />
                    )}
                </div>
            </div>
        );
    }
}

ConnectionModal.propTypes = {
    connectionIconURL: PropTypes.string,
    connectionModalExtensionId: PropTypes.string,
    connectionModalPeripheralId: PropTypes.string,
    connectionModalPeripheralName: PropTypes.string,
    connectionSmallIconURL: PropTypes.string,
    connectionTipIconURL: PropTypes.string,
    extensionId: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onConnecting: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(Object).isRequired
};

export default ConnectionModal;
export {PHASES};
